import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AA_NORMAL, readableOnAll, worstContrast } from '@/shared/lib/color';
import { Button, ColorSwatches } from '@/shared/ui';

import styles from './editor.module.css';

export interface TextColorField {
  key: string;
  label: string;
  /** `text` is checked for contrast against its backdrop; `surface` is a backdrop itself. */
  role: 'text' | 'surface';
}

interface Props {
  fields: readonly TextColorField[];
  /** Current colors by field key (any object with the fields' keys). */
  values: object;
  onChange: (key: string, value: string | null) => void;
  /** Colors a text field ends up on (a gradient gives several samples). */
  backdrops: (key: string) => readonly string[];
  idPrefix: string;
}

/**
 * Explicit text colors and a backing surface. Everything starts "automatic" (a readable color is
 * derived from the background). A chosen text color is checked against WCAG AA (4.5:1) at the
 * WORST point of its background and gets a warning plus a one-click fix; nothing is blocked.
 */
export function TextColors({ fields, values, onChange, backdrops, idPrefix }: Props) {
  const { t } = useTranslation();
  // The fields are rendered only while the section is open (they are many swatch groups).
  const [open, setOpen] = useState(false);
  return (
    <details
      className={styles.textColors}
      data-testid={`${idPrefix}-text-colors`}
      open={open}
      onToggle={(event) => {
        setOpen(event.currentTarget.open);
      }}
    >
      <summary>{t('appearance.text.title')}</summary>
      {open && <p className={styles.hint}>{t('appearance.text.hint')}</p>}
      {open &&
        fields.map((field) => {
          const raw = (values as Record<string, unknown>)[field.key];
          const value = typeof raw === 'string' ? raw : null;
          const samples = backdrops(field.key);
          const ratio = value && field.role === 'text' ? worstContrast(value, samples) : null;
          const weak = ratio !== null && ratio < AA_NORMAL;
          const shown = ratio === null ? '' : ratio.toFixed(1);
          return (
            <div key={field.key} className={styles.textField}>
              <ColorSwatches
                label={field.label}
                value={value}
                noneLabel={t('appearance.text.auto')}
                onChange={(color) => {
                  onChange(field.key, color);
                }}
              />
              {field.role === 'text' && (
                <p
                  className={weak ? styles.contrastWarn : styles.hint}
                  role={weak ? 'alert' : undefined}
                  data-testid={`${idPrefix}-contrast-${field.key}`}
                >
                  {value === null
                    ? t('appearance.text.autoNote')
                    : weak
                      ? t('appearance.text.low', { ratio: shown })
                      : t('appearance.text.ok', { ratio: shown })}
                </p>
              )}
              {weak && (
                <Button
                  size="sm"
                  onClick={() => {
                    onChange(field.key, readableOnAll(samples));
                  }}
                >
                  {t('appearance.text.fix')}
                </Button>
              )}
            </div>
          );
        })}
    </details>
  );
}
